import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ShoppingBag,
  Bell,
  X,
  Mail,
  Phone,
  User,
  Clock,
  Eye,
  MessageSquare,
  ShoppingCart,
  Download,
  ExternalLink,
  Calendar,
  CheckCircle2,
  Send,
  Loader2,
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import {
  useLazyAbandonedCartQuery,
  useLazyAbandonedCartDashboardQuery,
  useSendReminderMutation,
  useSendBulkRemindersMutation,
  useImpersonetCustomerMutation,
  useAbandonedCommentMutation,
} from "../../../redux/actions/Admin/abandonedCartAdmin";
import { useAlert } from "../../../components/customer/AlertContext";
import { AbandonedCartDetailsDrawer } from "./AbandonedCartDetailsDrawer";
import { ABANDONED_CART_EXPORT, ABANDONED_CARTS } from "../../../config/apiUrlsAdmin";

export const PetlandAbandonedCartScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showConfirm } = useAlert();

  const [triggerFetchDashboard, { data: dashboardData }] = useLazyAbandonedCartDashboardQuery();
  const [triggerFetchList, { data: listData, isLoading: isListLoading }] = useLazyAbandonedCartQuery();

  const [sendSingleReminder] = useSendReminderMutation();
  const [sendBulkReminders, { isLoading: isBulkSending }] = useSendBulkRemindersMutation();
  const [impersonateCustomer, { isLoading: isImpersonating }] = useImpersonetCustomerMutation();
  const [addAbandonedComment, { isLoading: isAddingComment }] = useAbandonedCommentMutation();

  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [cartTypeFilter, setCartTypeFilter] = useState<"Product" | "Pet" | "Service" | "">("");
  const [selectedCartIds, setSelectedCartIds] = useState<number[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Comment Modal State
  const [activeCommentCart, setActiveCommentCart] = useState<any | null>(null);
  const [commentText, setCommentText] = useState("");

  const refreshCartData = () => {
    triggerFetchList({
      search: search || undefined,
      cartType: cartTypeFilter || null,
      pageNumber: 1,
      pageSize: 50,
    });
  };

  useEffect(() => {
    triggerFetchDashboard();
  }, [triggerFetchDashboard]);

  useEffect(() => {
    refreshCartData();
  }, [search, cartTypeFilter, triggerFetchList]);

  // Safe Date Formatter
  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr || dateStr.startsWith("0001-01-01")) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "—";

      return date.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "—";
    }
  };

  // Peek / Impersonate Action
  const handlePeekCustomerCart = async (userId: number, customerName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    try {
      await impersonateCustomer({ customerId: userId }).unwrap();
      navigate("/cartpage/customercartpage");
    } catch (err) {
      console.error("Failed to impersonate customer:", err);
      showConfirm({
        type: "error",
        title: "Action Failed",
        message: "Unable to inspect customer cart at this moment.",
        confirmText: "OK",
        showCancel: false,
      });
    }
  };

  // Export Logic
  const handleExportAbandonedCarts = async (userId?: number, customerName?: string) => {
    try {
      setIsExporting(true);

      const ENDPOINT = userId
        ? `${ABANDONED_CARTS}/${userId}/export`
        : ABANDONED_CART_EXPORT;

      const response = await fetch(ENDPOINT, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to download abandoned cart report.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileLabel = userId && customerName
        ? `Abandoned_Cart_${customerName.replace(/\s+/g, "_")}`
        : "All_Customers_Abandoned_Carts";

      link.download = `Petland_${fileLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showConfirm({
        type: "success",
        title: "Export Successful",
        message: userId
          ? `Cart report for ${customerName} exported successfully.`
          : "All abandoned carts data exported successfully.",
        confirmText: "OK",
        showCancel: false,
      });
    } catch (error) {
      console.error("Export failed:", error);
      showConfirm({
        type: "error",
        title: "Export Failed",
        message: "Something went wrong while attempting to download the export file.",
        confirmText: "OK",
        showCancel: false,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkAlert = () => {
    showConfirm({
      type: "success",
      title: "Send Bulk Reminders?",
      message: "Are you sure you want to send reminders to all users with abandoned carts?",
      confirmText: "Yes, Remind All",
      showCancel: true,
      onConfirm: async () => {
        try {
          await sendBulkReminders({ sendToAll: true, userIds: selectedCartIds }).unwrap();
          triggerFetchDashboard();
          refreshCartData();
          showConfirm({
            type: "success",
            title: "Reminders Sent",
            message: "Reminders sent to users successfully.",
            confirmText: "OK",
            showCancel: false,
          });
        } catch (err) {
          console.error(err);
          showConfirm({
            type: "error",
            title: "Failed",
            message: "Unable to send bulk reminders.",
            confirmText: "OK",
            showCancel: false,
          });
        }
      },
    });
  };

  const handleSingleAlert = (userId: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showConfirm({
      type: "success",
      title: "Send Reminder?",
      message: "Send an abandoned cart notification to this user?",
      confirmText: "Send",
      showCancel: true,
      onConfirm: async () => {
        try {
          await sendSingleReminder(userId).unwrap();
          refreshCartData();
          showConfirm({
            type: "success",
            title: "Reminder Sent",
            message: "Reminder notification sent successfully.",
            confirmText: "OK",
            showCancel: false,
          });
        } catch (err:any) {
          console.error(err);
          showConfirm({
            type: "error",
            title: "Failed",
            message: err?.data?.Message ||"Unable to send reminder.",
            confirmText: "OK",
            showCancel: false,
          });
        }
      },
    });
  };

  // Submit Admin Comment
  const handleSaveComment = async () => {
    if (!commentText.trim() || !activeCommentCart) return;

    const cartId = activeCommentCart.abandonedCartId || activeCommentCart.userId;

    try {
      await addAbandonedComment({
        abandonedCartId: cartId,
        data: {
          abandonedCartId: cartId,
          comment: commentText.trim(),
        },
      }).unwrap();

      setCommentText("");
      setActiveCommentCart(null);
      refreshCartData();

      showConfirm({
        type: "success",
        title: "Comment Added",
        message: "Admin comment saved successfully.",
        confirmText: "OK",
        showCancel: false,
      });
    } catch (err: any) {
      console.error("Failed to add comment:", err);
      showConfirm({
        type: "error",
        title: "Failed to Add Comment",
        message: err?.data?.message || "Could not save comment at this moment.",
        confirmText: "OK",
        showCancel: false,
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCartIds(carts.map((c: any) => c.userId));
    } else {
      setSelectedCartIds([]);
    }
  };

  const handleSelectRow = (userId: number, checked: boolean) => {
    if (checked) {
      setSelectedCartIds((prev) => [...prev, userId]);
    } else {
      setSelectedCartIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  const stats = dashboardData?.data;
  const carts = listData?.data?.data || [];
  const isAllSelected = carts.length > 0 && selectedCartIds.length === carts.length;

  return (
    <div className="min-h-screen bg-slate-50/50 px-4 py-6 text-left relative overflow-x-hidden">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Abandoned Carts
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-1">
              Review customer shopping carts, preview added items, track comments, and dispatch recovery notifications.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CommonButton
              text={isExporting ? "Exporting..." : "Export All Carts"}
              variant="outline"
              icon={<Download className="w-4 h-4" />}
              className="border-emerald-200 text-emerald-700 font-bold text-xs bg-emerald-50/50 hover:bg-emerald-50 px-4 py-2.5 rounded-xl shadow-xs cursor-pointer"
              disabled={isExporting || carts.length === 0}
              onClick={() => handleExportAbandonedCarts()}
            />

            <CommonButton
              text={isBulkSending ? "Sending..." : "Remind All"}
              variant="filled"
              icon={<Bell className="w-4 h-4" />}
              className="bg-[#2F5E2E] font-bold text-xs text-white px-4 py-2.5 rounded-xl shadow-xs cursor-pointer hover:bg-[#254b24]"
              disabled={isBulkSending || carts.length === 0}
              onClick={handleBulkAlert}
            />
          </div>
        </div>

        {/* Summary Stats Header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200/80 p-4 rounded-2xl shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Abandoned Carts</span>
            <p className="text-xl font-black text-slate-900 mt-0.5">{stats?.totalAbandonedCustomers || 0}</p>
          </div>
          <div className="bg-white border border-slate-200/80 p-4 rounded-2xl shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Lost Revenue</span>
            <p className="text-xl font-black text-emerald-600 mt-0.5">AED {(stats?.totalCartValue || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-slate-200/80 p-4 rounded-2xl shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Average Cart Value</span>
            <p className="text-xl font-black text-slate-900 mt-0.5">AED {(stats?.averageCartValue || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-slate-200/80 p-4 rounded-2xl shadow-xs">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Items Left</span>
            <p className="text-xl font-black text-[#2F5E2E] mt-0.5">{stats?.totalItems || 0}</p>
          </div>
        </div>

        {/* Toolbar & Filters */}
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full md:w-auto flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by Customer Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:outline-none focus:border-[#2F5E2E]"
              />
            </div>
            <select
              value={cartTypeFilter}
              onChange={(e) => setCartTypeFilter(e.target.value as any)}
              className="px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50 font-medium outline-none cursor-pointer"
            >
              <option value="">All Types</option>
              <option value="Product">Products</option>
              <option value="Pet">Pets</option>
              <option value="Service">Services</option>
            </select>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <button
              type="button"
              onClick={handleBulkAlert}
              disabled={selectedCartIds.length === 0}
              className="px-4 py-2 bg-logoHeading text-white rounded-xl font-bold text-xs hover:bg-iconColor disabled:opacity-40 transition cursor-pointer"
            >
              SEARCH & REMIND ({selectedCartIds.length})
            </button>
            <button
              type="button"
              onClick={() => setSelectedCartIds([])}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:bg-slate-300 transition cursor-pointer"
            >
              DESELECT ALL
            </button>
          </div>
        </div>

        {/* Main List Container */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="bg-slate-100/70 border-b border-slate-200 px-6 py-3.5 grid grid-cols-12 text-xs font-black text-slate-600 uppercase tracking-wider">
            <div className="col-span-12 md:col-span-4 flex items-center gap-3">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="w-4 h-4 rounded accent-[#2F5E2E] cursor-pointer"
              />
              <span>Customer Info</span>
            </div>
            <div className="hidden md:block md:col-span-5">Shopping Cart Preview</div>
            <div className="hidden md:block md:col-span-3 text-right pr-4">Action</div>
          </div>

          <div className="divide-y divide-slate-100">
            {isListLoading ? (
              <div className="p-12 text-center text-xs text-slate-400 font-medium">Loading customer carts...</div>
            ) : carts.length === 0 ? (
              <div className="p-12 text-center text-xs text-slate-400 font-medium">No abandoned carts found.</div>
            ) : (
              carts.map((cart: any) => (
                <div
                  key={cart.userId}
                  className="p-5 grid grid-cols-12 gap-4 items-start hover:bg-slate-50/80 transition-colors"
                >
                  {/* COLUMN 1: CUSTOMER INFO & TIMESTAMPS */}
                  <div className="col-span-12 md:col-span-4 flex gap-3">
                    <input
                      type="checkbox"
                      checked={selectedCartIds.includes(cart.userId)}
                      onChange={(e) => handleSelectRow(cart.userId, e.target.checked)}
                      className="w-4 h-4 mt-1 rounded accent-[#2F5E2E] cursor-pointer shrink-0"
                    />
                    <div className="space-y-2 text-xs text-slate-700 min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 font-bold text-slate-900 text-sm">
                        <User className="w-3.5 h-3.5 text-[#2F5E2E] shrink-0" />
                        <span className="truncate">{cart.customerName || "Guest User"}</span>
                      </div>

                      <div className="space-y-1 text-slate-500 font-medium pl-0.5">
                        <p className="flex items-center gap-1.5 truncate">
                          <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>{cart.email || "No email"}</span>
                        </p>
                        <p className="flex items-center gap-1.5 truncate">
                          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>{cart.phoneNumber || "No contact number"}</span>
                        </p>
                      </div>

                      {/* Date & Meta Info Box */}
                      <div className="bg-slate-50 border border-slate-200/80 p-2.5 rounded-xl space-y-1.5 text-[11px] font-semibold text-slate-600 mt-2">
                        <div className="flex justify-between items-center border-b border-slate-200/60 pb-1">
                          <span className="text-slate-400 uppercase text-[9px] font-bold">Cart Type:</span>
                          <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-extrabold text-[10px]">
                            {cart.cartType}
                          </span>
                        </div>

                        {/* Notification Sent Status */}
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 uppercase text-[9px] font-bold">Notification:</span>
                          {cart.notificationSent ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-md text-[10px] font-bold">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              Sent ({formatDateTime(cart.notificationSentOn)})
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[10px] font-medium">Not sent</span>
                          )}
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 uppercase text-[9px] font-bold">First Visit:</span>
                          <span className="text-slate-700 font-bold">{formatDateTime(cart.firstVisit)}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 uppercase text-[9px] font-bold">Last Visit:</span>
                          <span className="text-slate-700 font-bold">{formatDateTime(cart.lastVisit)}</span>
                        </div>

                        {/* Existing Comments Summary */}
                        {cart.comments && cart.comments.length > 0 && (
                          <div className="pt-1.5 border-t border-slate-200/60">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="text-amber-700 font-bold flex items-center gap-1">
                                <MessageSquare className="w-3 h-3 text-amber-600" />
                                {cart.comments.length} Admin Comment{cart.comments.length > 1 ? "s" : ""}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-500 italic mt-0.5 truncate max-w-[280px]">
                              "{cart.comments[cart.comments.length - 1]?.comment || cart.comments[cart.comments.length - 1]}"
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* COLUMN 2: SHOPPING CART PREVIEW */}
                  <div className="col-span-12 md:col-span-5 border-t md:border-t-0 md:border-x border-slate-100 pt-3 md:pt-0 md:px-4 space-y-2">
                    <div className="space-y-1.5">
                      {cart.previewItems && cart.previewItems.length > 0 ? (
                        cart.previewItems.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className="bg-white border border-[#DDE7D8] rounded-xl p-2.5 flex items-center justify-between gap-2 text-xs shadow-2xs"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              {item.imageUrl ? (
                                <img
                                  src={item.imageUrl}
                                  alt={item.name}
                                  className="w-10 h-10 object-cover rounded-lg border border-slate-100 shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                                  <ShoppingCart className="w-4 h-4 text-slate-400" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <h5 className="font-bold text-slate-900 truncate text-[11px] leading-tight">
                                  {item.name}
                                </h5>
                                {item.variantName && (
                                  <p className="text-[10px] text-slate-400 truncate">{item.variantName}</p>
                                )}
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              <span className="text-[10px] font-bold text-slate-400 block">x {item.quantity}</span>
                              <span className="font-extrabold text-slate-900 text-xs">
                                AED {(item.totalPrice || item.unitPrice * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-slate-400 italic">No item previews available.</p>
                      )}
                    </div>

                    {/* Total Summary Bar */}
                    <div className="bg-slate-100/80 border border-slate-200 rounded-xl p-2.5 flex justify-between items-center text-xs font-black text-slate-900">
                      <span>Total :</span>
                      <div className="flex items-center gap-4">
                        <span className="text-slate-500 font-bold">x {cart.totalItems}</span>
                        <span className="text-[#2F5E2E] font-black">AED {cart.cartValue.toFixed(2)}</span>
                      </div>
                    </div>

                    {cart.remainingItems > 0 && (
                      <button
                        onClick={() => setSelectedUserId(cart.userId)}
                        className="text-[11px] font-bold text-[#2F5E2E] hover:underline block pt-1 cursor-pointer"
                      >
                        + {cart.remainingItems} more items inside cart...
                      </button>
                    )}
                  </div>

                  {/* COLUMN 3: ACTIONS & STATUS */}
                  <div className="col-span-12 md:col-span-3 flex flex-col items-end justify-between self-stretch pt-3 md:pt-0 border-t md:border-t-0 border-slate-100 space-y-3">
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {/* Remind Icon */}
                      <button
                        type="button"
                        title="Send Notification Reminder"
                        onClick={(e) => handleSingleAlert(cart.userId, e)}
                        className="w-8 h-8 rounded-full bg-logoHeading hover:bg-iconColor text-white flex items-center justify-center transition shadow-xs cursor-pointer"
                      >
                        <Bell className="w-4 h-4" />
                      </button>

                      {/* Peek / Impersonate Icon */}
                      <button
                        type="button"
                        title="Peek Into Customer Cart"
                        disabled={isImpersonating}
                        onClick={(e) => handlePeekCustomerCart(cart.userId, cart.customerName, e)}
                        className="w-8 h-8 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition shadow-xs cursor-pointer disabled:opacity-50"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>

                      {/* Detail Drawer Open Icon */}
                      <button
                        type="button"
                        title="Show Details Drawer"
                        onClick={() => setSelectedUserId(cart.userId)}
                        className="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition shadow-xs cursor-pointer"
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {/* Export Single User Cart Data Icon */}
                      <button
                        type="button"
                        title="Export User Cart Statement"
                        onClick={() => handleExportAbandonedCarts(cart.userId, cart.customerName)}
                        className="w-8 h-8 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white flex items-center justify-center transition shadow-xs cursor-pointer"
                      >
                        <Download className="w-4 h-4" />
                      </button>

                      {/* Comment Action Button */}
                      <button
                        type="button"
                        title="Add/View Comments"
                        onClick={() => setActiveCommentCart(cart)}
                        className={`relative w-8 h-8 rounded-full flex items-center justify-center transition shadow-xs cursor-pointer ${
                          cart.comments && cart.comments.length > 0
                            ? "bg-amber-500 hover:bg-amber-600 text-white"
                            : "bg-slate-600 hover:bg-slate-700 text-white"
                        }`}
                      >
                        <MessageSquare className="w-4 h-4" />
                        {cart.comments && cart.comments.length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white">
                            {cart.comments.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Side Drawer for Full Cart Details */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${
          selectedUserId ? "opacity-100 pointer-events-auto" : "opacity-0"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/30 backdrop-blur-xs transition-opacity"
          onClick={() => setSelectedUserId(null)}
        />

        <div
          className={`absolute right-0 top-0 bottom-0 w-full sm:w-[500px] bg-white shadow-2xl transition-transform duration-300 transform border-l border-slate-200 flex flex-col ${
            selectedUserId ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {selectedUserId && (
            <div className="p-6 flex flex-col h-full">
              <div className="flex justify-between items-center border-b pb-4 mb-4">
                <h3 className="font-black text-slate-900 text-base">Cart Inspection Detail Page</h3>
                <button
                  onClick={() => setSelectedUserId(null)}
                  className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
                >
                  <X className="w-4 h-4 text-slate-700" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto pr-1">
                <AbandonedCartDetailsDrawer userId={selectedUserId} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin Comment Modal */}
      {activeCommentCart && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-amber-600" />
                <h4 className="font-bold text-sm text-slate-800">
                  Cart Comments: {activeCommentCart.customerName || "Customer"}
                </h4>
              </div>
              <button
                onClick={() => {
                  setActiveCommentCart(null);
                  setCommentText("");
                }}
                className="p-1 hover:bg-slate-200 rounded-lg text-slate-500 cursor-pointer transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Existing Comments List */}
            <div className="p-5 overflow-y-auto flex-1 space-y-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                Comment History
              </span>
              {activeCommentCart.comments && activeCommentCart.comments.length > 0 ? (
                activeCommentCart.comments.map((cmt: any, idx: number) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200/80 p-3 rounded-xl text-xs space-y-1">
                    <p className="text-slate-700 font-medium">{cmt.comment || (typeof cmt === "string" ? cmt : JSON.stringify(cmt))}</p>
                    {cmt.createdAt && (
                      <span className="text-[10px] text-slate-400 block">{formatDateTime(cmt.createdAt)}</span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400 italic py-2">No comments added for this cart yet.</p>
              )}

              {/* Add New Comment */}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <label className="text-xs font-bold text-slate-700 block">Add New Admin Note</label>
                <textarea
                  rows={3}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Type customer follow-up notes, phone call logs, or reason for abandonment..."
                  className="w-full border border-slate-200 rounded-xl p-3 text-xs bg-slate-50 focus:bg-white focus:outline-none focus:border-amber-500 transition resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setActiveCommentCart(null);
                  setCommentText("");
                }}
                className="px-4 py-2 rounded-xl text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-white transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isAddingComment || !commentText.trim()}
                onClick={handleSaveComment}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-xs"
              >
                {isAddingComment ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Post Comment</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};